import { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
// Import Types
import type { PartnerCustomField } from 'types';
// Import UI Components
import { Section } from '@geneui/components';
// Import Components
import CampaignGeneralDetails from './CampaignGeneralDetails';
import SchedulerWithModal from 'components/common/SchedulerWithModal';
import CustomFields from 'components/common/CustomFields';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { ObjectTypeIdsEnum } from 'enums';

const CampaignDetails = ({
    startDate,
    endDate,
    startTime,
    setStartDate,
    setEndDate,
    setStartTime,
    selectedDateSelector,
    setSelectedDateSelector,
    isVisibleRecCount,
    setIsVisibleRecCount,
    recurrenceData,
    isLoaded,
    datesDict,
    setDatesDict,
    getRecCount,
    selectedProvider,
    selectedGoalType,
    analysisDate,
    setAnalysisDate,
    setSelectedProvider,
    setSelectedGoalType,
    goalCount,
    setGoalCount,
    customFieldsValues,
    setCustomFieldsValues,
    selectedChannel,
    isNonMarketing,
    setIsNonMarketing,
    isPublic,
    setIsPublic,
    addValidationProperties,
    setDropDownSettings,
    dropDownSettings,
    pageType,
    campaignStatus,
    timeZone,
    setTimeZone,
    isActiveSchedule,
    setIsActiveSchedule,
    campaignNextRunDate,
    campaignLastRunDate,
}) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const [customFields, setCustomFields] = useState<Nullable<PartnerCustomField[]>>(null);

    const { getPartnerCustomFieldsRequest } = useCancelableRequests({
        getPartnerCustomFieldsRequest: UtilsHttpService.getPartnerCustomFields,
    });

    const getPartnerCustomFields = () => {
        doPostRequest(getPartnerCustomFieldsRequest.request, {
            queryString: { objectType: ObjectTypeIdsEnum.Campaign },
            successCallback: (Data) => {
                setCustomFields(Data);
            },
        });
    };

    const init = () => {
        getPartnerCustomFields();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="crm-campaign-details">
            <Section text="">
                <ul className="module-header cr-position-radius p-top s-big">
                    <li className="left-content">
                        <div className="left-inner">
                            <div className="module-title ellipsis-text" title="">
                                <div className="crm-statics-segment-data-table-name">{t(l.GeneralDetails)}</div>
                            </div>
                        </div>
                    </li>
                </ul>
                <CampaignGeneralDetails
                    selectedProvider={selectedProvider}
                    selectedGoalType={selectedGoalType}
                    setSelectedProvider={setSelectedProvider}
                    setSelectedGoalType={setSelectedGoalType}
                    analysisDate={analysisDate}
                    setAnalysisDate={setAnalysisDate}
                    goalCount={goalCount}
                    setGoalCount={setGoalCount}
                    selectedChannel={selectedChannel}
                    isNonMarketing={isNonMarketing}
                    setIsNonMarketing={setIsNonMarketing}
                    isPublic={isPublic}
                    setIsPublic={setIsPublic}
                />
            </Section>
            {!isNil(customFields) && !isEmpty(customFields) && (
                <Section text="">
                    <ul className="module-header cr-position-radius p-top s-big">
                        <li className="left-content">
                            <div className="left-inner">
                                <div className="module-title ellipsis-text" title="">
                                    <div className="crm-statics-segment-data-table-name">{t(l.CustomFields)}</div>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <div className="crm-create-campaign-custom-fields-body-container">
                        <CustomFields
                            customFieldsValues={customFieldsValues}
                            setCustomFieldsValues={setCustomFieldsValues}
                            customFields={customFields}
                            isLoaded={isLoaded}
                            addValidationProperties={addValidationProperties}
                            setDropDownSettings={setDropDownSettings}
                            dropDownSettings={dropDownSettings}
                        />
                    </div>
                </Section>
            )}
            <Section text="">
                <ul className="module-header cr-position-radius p-top s-big">
                    <li className="left-content">
                        <div className="left-inner">
                            <div className="module-title ellipsis-text" title="">
                                <div className="crm-statics-segment-data-table-name">{t(l.Schedule)}</div>
                            </div>
                        </div>
                    </li>
                </ul>
                <SchedulerWithModal
                    startDate={startDate}
                    endDate={endDate}
                    startTime={startTime}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setStartTime={setStartTime}
                    selectedDateSelector={selectedDateSelector}
                    setSelectedDateSelector={setSelectedDateSelector}
                    isVisibleRecCount={isVisibleRecCount}
                    setIsVisibleRecCount={setIsVisibleRecCount}
                    recurrenceData={recurrenceData}
                    isLoaded={isLoaded}
                    datesDict={datesDict}
                    setDatesDict={setDatesDict}
                    getRecCount={getRecCount}
                    pageType={pageType}
                    campaignStatus={campaignStatus}
                    timeZone={timeZone}
                    setTimeZone={setTimeZone}
                    isActiveSchedule={isActiveSchedule}
                    setIsActiveSchedule={setIsActiveSchedule}
                    schedulerOffInfoMessage={t(l.CampaignSchedulerOffInfoMessage)}
                    campaignNextRunDate={campaignNextRunDate}
                    campaignLastRunDate={campaignLastRunDate}
                />
            </Section>
        </div>
    );
};

CampaignDetails.propTypes = {
    isNonMarketing: PropTypes.bool,
    isPublic: PropTypes.bool,
    isLoaded: PropTypes.bool,
    isVisibleRecCount: PropTypes.bool,
    recurrenceData: PropTypes.object,
    goalCount: PropTypes.number,
    selectedProvider: PropTypes.number,
    selectedChannel: PropTypes.string,
    selectedDateSelector: PropTypes.string,
    analysisDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    selectedGoalType: PropTypes.string,
    schedule: PropTypes.object,
    datesDict: PropTypes.object,
    customFieldsValues: PropTypes.array,
    setSelectedProvider: PropTypes.func,
    setSelectedGoalType: PropTypes.func,
    setGoalCount: PropTypes.func,
    setAnalysisDate: PropTypes.func,
    setCustomFieldsValues: PropTypes.func,
    setSelectedDateSelector: PropTypes.func,
    setDatesDict: PropTypes.func,
    setIsVisibleRecCount: PropTypes.func,
    setSchedule: PropTypes.func,
    getRecCount: PropTypes.func,
    startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    startTime: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    setStartDate: PropTypes.func,
    setEndDate: PropTypes.func,
    setStartTime: PropTypes.func,
    setIsNonMarketing: PropTypes.func,
    setIsPublic: PropTypes.func,
    addValidationProperties: PropTypes.func,
    setDropDownSettings: PropTypes.func,
    dropDownSettings: PropTypes.array,
    pageType: PropTypes.string,
    campaignStatus: PropTypes.number,
    timeZone: PropTypes.number,
    setTimeZone: PropTypes.func,
    isActiveSchedule: PropTypes.bool,
    setIsActiveSchedule: PropTypes.func,
    campaignNextRunDate: PropTypes.string,
    campaignLastRunDate: PropTypes.string,
};

CampaignDetails.defaultProps = {
    campaignStatus: null,
};
export default memo(CampaignDetails);
